import { useKeycloak } from '@react-keycloak/web';
import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CheckIcon from 'images/v5/icons/check.svg';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import { selectLayout } from 'store/layoutSlice';
import { selectOrganizations, selectTennantData } from 'store/userSlice';
import { getActiveOrganizationId, switchOrganization } from 'utils/activeOrganization';
import styles from './OrganizationSwitcher.module.scss';
/**
 * Lets a user move between the organizations they belong to.
 *
 * Selecting one stores its id, which every subsequent request sends as X-Organization-Id, and
 * reloads the application (see switchOrganization). The backend re-verifies membership on each
 * call, so this component only expresses intent - it cannot grant access to anything.
 * Organizations that are not ready yet are listed but cannot be selected.
 *
 * Renders nothing when the user belongs to fewer than two organizations: a switcher with one
 * entry is noise, and the single organization is already the active one.
 */
function OrganizationSwitcher() {
    var _a, _b, _c;
    const { t } = useTranslation(['authentication']);
    const { keycloak } = useKeycloak();
    const layoutState = useSelector(selectLayout);
    const organizations = useSelector(selectOrganizations);
    const currentTenant = useSelector(selectTennantData);
    const activeId = (_a = currentTenant === null || currentTenant === void 0 ? void 0 : currentTenant.id) !== null && _a !== void 0 ? _a : getActiveOrganizationId();
    const active = (_b = organizations.find((organization) => organization.id === activeId)) !== null && _b !== void 0 ? _b : organizations[0];
    if (!organizations || organizations.length < 2) {
        return null;
    }
    const switchTo = (organizationId) => {
        if (organizationId === (active === null || active === void 0 ? void 0 : active.id)) {
            return;
        }
        switchOrganization(organizationId, () => keycloak.updateToken(-1));
    };
    const initials = (name) => name.trim().slice(0, 2);
    return (React.createElement(Dropdown, { size: "m", position: "menubar", trigger: React.createElement("button", { type: "button", className: classNames(styles.wrap, { [styles.full]: layoutState.showMenu }), title: active === null || active === void 0 ? void 0 : active.name, "data-testid": "organization-switcher" },
            React.createElement("span", { className: styles.icon }, initials((_c = active === null || active === void 0 ? void 0 : active.name) !== null && _c !== void 0 ? _c : '?')),
            layoutState.showMenu && React.createElement("span", { className: styles.label }, active === null || active === void 0 ? void 0 : active.name)) },
        React.createElement("div", { className: styles.list }, organizations.map((organization) => {
            const ready = organization.provisioningStatus === 'READY';
            return (React.createElement("button", { key: organization.id, type: "button", className: classNames(styles.item, {
                    [styles.active]: organization.id === (active === null || active === void 0 ? void 0 : active.id),
                    [styles.disabled]: !ready,
                }), onClick: () => switchTo(organization.id), disabled: !ready, title: organization.name },
                React.createElement("span", { className: styles.itemName }, organization.name),
                !ready && React.createElement("span", { className: styles.status }, t('organization_not_ready')),
                organization.id === (active === null || active === void 0 ? void 0 : active.id) && React.createElement(CheckIcon, null)));
        }))));
}
export default OrganizationSwitcher;
